import React, { useEffect, useState } from 'react';
import { Header } from '../layout/Header';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../context/AuthContext';
import API_URL from '../config/API_URL';

export const ManageProfilePage = () => {
  const { user } = useAuth();
  const { showToast, ToastContainer } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [errors, setErrors] = useState({});

  // Single fetch, runs ONCE
  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/me`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        console.log('API /me response:', data); // Debug
        setForm({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          email: data.email || '',
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
      } catch (err) {
        console.error('Fetch error:', err);
        const msg = err.message.includes('billion') || err.message.includes('pounds')
          ? 'Profile load failed – server currency error (billion pounds)'
          : 'Could not load profile';
        showToast(msg, 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []); // Empty deps = one call

  const validateProfile = (values) => {
    const errors = {};
    if (!values.first_name.trim()) errors.first_name = 'First name required';
    if (!values.last_name.trim()) errors.last_name = 'Last name required';
    if (!values.email.trim()) errors.email = 'Email required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) errors.email = 'Invalid email';
    return errors;
  };

  const validatePassword = (values) => {
    const errors = {};
    if (!values.current_password) errors.current_password = 'Current password required';
    if (values.new_password.length < 5) errors.new_password = 'Password must be 8+ chars';
    if (values.new_password !== values.confirm_password) errors.confirm_password = 'Passwords don’t match';
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const profileErrors = validateProfile(form);
    if (Object.keys(profileErrors).length) {
      setErrors(profileErrors);
      showToast('Fix profile errors', 'error');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/me`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim()
        })
      });

      const data = await (res.headers.get('content-type')?.includes('application/json')
        ? res.json()
        : { error: await res.text() });

      if (!res.ok) {
        console.error('PUT error:', data);
        const msg = data?.errors
          ? Object.values(data.errors).flat().join(' ')
          : data?.error || `Save failed: ${res.status}`;
        showToast(msg.includes('billion') || msg.includes('pounds')
          ? 'Save failed – server currency error (billion pounds)'
          : msg, 'error');
        setErrors(data?.errors || {});
        return;
      }

      setForm((prev) => ({
        ...prev,
        first_name: data.first_name || prev.first_name,
        last_name: data.last_name || prev.last_name,
        email: data.email || prev.email
      }));
      showToast('Profile updated', 'success');
    } catch (err) {
      console.error('Save error:', err);
      showToast('Network error – could not save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    const pwErrors = validatePassword(form);
    if (Object.keys(pwErrors).length) {
      setErrors(pwErrors);
      showToast('Fix password errors', 'error');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/me/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_password: form.current_password,
          new_password: form.new_password
        })
      });

      const data = await (res.headers.get('content-type')?.includes('application/json')
        ? res.json()
        : { error: await res.text() });

      if (!res.ok) {
        console.error('Password error:', data);
        showToast(data?.error || 'Could not change password', 'error');
        setErrors(data?.errors || {});
        return;
      }

      showToast('Password updated', 'success');
      setForm((prev) => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: ''
      }));
      setErrors({});
    } catch (err) {
      console.error('Password error:', err);
      showToast('Network error – could not update password', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fce7f3 0%, #dbeafe 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontSize: '1.25rem', color: '#6b7280' }}>Loading profile...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fce7f3 0%, #dbeafe 100%)' }}>
      <ToastContainer />
      <Header userName={`${user.first_name} ${user.last_name}`} userType="parent" />
      <div style={{ maxWidth: '960px', margin: '0 auto', padding: '2rem 1.5rem', display: 'grid', gap: '1.5rem' }}>
        <Card style={{ padding: '1.5rem' }}>
          <form onSubmit={handleSave}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '1rem' }}>Profile</h2>
            <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
              <div>
                <label style={{ display: 'block', color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.25rem' }}>First Name *</label>
                <input
                  name="first_name"
                  type="text"
                  value={form.first_name}
                  onChange={handleChange}
                  maxLength={50}
                  style={{ width: '100%', padding: '0.625rem', borderRadius: '0.5rem', border: `1px solid ${errors.first_name ? '#ef4444' : '#d1d5db'}` }}
                />
                {errors.first_name && <p style={{ marginTop: '0.25rem', color: '#ef4444', fontSize: '0.875rem' }}>{errors.first_name}</p>}
              </div>
              <div>
                <label style={{ display: 'block', color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.25rem' }}>Last Name *</label>
                <input
                  name="last_name"
                  type="text"
                  value={form.last_name}
                  onChange={handleChange}
                  maxLength={50}
                  style={{ width: '100%', padding: '0.625rem', borderRadius: '0.5rem', border: `1px solid ${errors.last_name ? '#ef4444' : '#d1d5db'}` }}
                />
                {errors.last_name && <p style={{ marginTop: '0.25rem', color: '#ef4444', fontSize: '0.875rem' }}>{errors.last_name}</p>}
              </div>
            </div>
            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.25rem' }}>Email *</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                maxLength={120}
                style={{ width: '100%', padding: '0.625rem', borderRadius: '0.5rem', border: `1px solid ${errors.email ? '#ef4444' : '#d1d5db'}` }}
              />
              {errors.email && <p style={{ marginTop: '0.25rem', color: '#ef4444', fontSize: '0.875rem' }}>{errors.email}</p>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
              <Button type="submit" disabled={saving} style={{ padding: '0.5rem 1.5rem', backgroundColor: saving ? '#93c5fd' : '#2563eb', color: 'white', borderRadius: '0.5rem', opacity: saving ? 0.5 : 1 }}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </Card>
        <Card style={{ padding: '1.5rem' }}>
          <form onSubmit={handlePasswordChange}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '1rem' }}>Change Password</h2>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.25rem' }}>Current Password *</label>
                <input
                  name="current_password"
                  type="password"
                  value={form.current_password}
                  onChange={handleChange}
                  style={{ width: '100%', padding: '0.625rem', borderRadius: '0.5rem', border: `1px solid ${errors.current_password ? '#ef4444' : '#d1d5db'}` }}
                />
                {errors.current_password && <p style={{ marginTop: '0.25rem', color: '#ef4444', fontSize: '0.875rem' }}>{errors.current_password}</p>}
              </div>
              <div>
                <label style={{ display: 'block', color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.25rem' }}>New Password *</label>
                <input
                  name="new_password"
                  type="password"
                  value={form.new_password}
                  onChange={handleChange}
                  placeholder="At least 5 characters"
                  style={{ width: '100%', padding: '0.625rem', borderRadius: '0.5rem', border: `1px solid ${errors.new_password ? '#ef4444' : '#d1d5db'}` }}
                />
                {errors.new_password && <p style={{ marginTop: '0.25rem', color: '#ef4444', fontSize: '0.875rem' }}>{errors.new_password}</p>}
              </div>
              <div>
                <label style={{ display: 'block', color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.25rem' }}>Confirm New Password *</label>
                <input
                  name="confirm_password"
                  type="password"
                  value={form.confirm_password}
                  onChange={handleChange}
                  style={{ width: '100%', padding: '0.625rem', borderRadius: '0.5rem', border: `1px solid ${errors.confirm_password ? '#ef4444' : '#d1d5db'}` }}
                />
                {errors.confirm_password && <p style={{ marginTop: '0.25rem', color: '#ef4444', fontSize: '0.875rem' }}>{errors.confirm_password}</p>}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
              <Button type="submit" disabled={saving} style={{ padding: '0.5rem 1.5rem', backgroundColor: saving ? '#93c5fd' : '#16a34a', color: 'white', borderRadius: '0.5rem', opacity: saving ? 0.5 : 1 }}>
                {saving ? 'Updating...' : 'Update Password'}
              </Button>
            </div>
            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
              We’ll email you a confirmation when your password is changed.
            </p>
          </form>
        </Card>
      </div>
    </div>
  );
};